<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Bonus Type List
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <!-- Top Row: Search + Export + Add -->
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="5" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group variant="outlined" density="compact" size="x-small" divided>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="success" @click="exportToCSV">
                          CSV
                        </v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="primary" @click="exportToExcel">
                          EXCEL
                        </v-btn>
                        <v-btn prepend-icon="mdi mdi-download" variant="flat" color="red" @click="exportToPDF">
                          PDF
                        </v-btn>
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="3" cols="12">
                    <div class="text-right pr-2">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="openAddBonusType"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi mdi-plus"
                        rounded="xl"
                      >
                        Add Bonus Type
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>

                <!-- Bonus Type Table -->
                <v-data-table
                  :headers="headers"
                  :items="getSchema"
                  :loading="loading"
                  v-model:items-per-page="itemsPerPage"
                  :search="search"
                  :hover="true"
                  density="compact"
                >
                  <template v-slot:item.index="{ index }">{{ ++index }}</template>

                  <template v-slot:item.status="{ item }">
                    <v-chip v-if="item.status == 1" color="success" size="small" class="text-capitalize">
                      Active
                    </v-chip>
                    <v-chip v-else color="red" size="small" class="text-capitalize">
                      Inactive
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-icon class="me-2 edit-button" color="primary" @click="editItem(item)" v-if="$updateAccess($route.fullPath)">
                      mdi mdi-pencil-outline
                    </v-icon>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- ================= MODAL ================= -->
      <v-dialog v-model="localDialog" max-width="500px">
        <v-card class="pa-4">
          <v-card-title>{{ isEntryMode ? "Add Bonus Type" : "Update Bonus Type" }}</v-card-title>

          <v-card-text>
            <v-text-field v-model="name.value.value" label="Name" density="compact" variant="outlined" :error-messages="name.errorMessage.value" />
            <v-text-field v-model="remarks.value.value" label="Remarks" density="compact" variant="outlined" :error-messages="remarks.errorMessage.value" />
            <v-switch v-model="status.value.value" label="Active" :true-value="1" :false-value="0" />
          </v-card-text>

          <v-card-actions>
            <v-btn class="bg-brand w-15 ms-2" @click="submit">Save</v-btn>
            <v-btn class="bg-red" @click="closeDialog">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <!-- ================= MODAL END ================= -->
    </v-container>
  </v-app>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

export default {
  name: "BonusTypeSetup",
  props: {
    dialog: Boolean,
  },
  setup(props, { emit }) {
    const store = useStore();
    const localDialog = ref(false);
    const isEntryMode = ref(true);
    const loading = ref(false);

    const { handleSubmit, resetForm, setValues } = useForm({
      validationSchema: yup.object({
        name: yup.string().trim().required("Name is required").max(50),
        remarks: yup.string().trim().nullable().max(250),
        status: yup.number().required(),
      }),
      initialValues: {
        bonus_type_id: "",
        name: "",
        remarks: "",
        status: 1,
      },
    });

    const name = useField("name");
    const remarks = useField("remarks");
    const status = useField("status");
    const bonus_type_id = useField("bonus_type_id");

    const openAddBonusType = () => {
      resetForm({
        values: { bonus_type_id: "", name: "", remarks: "", status: 1 },
      });
      isEntryMode.value = true;
      localDialog.value = true;
    };

    const editItem = (item) => {
      setValues({
        bonus_type_id: item.bonus_type_id || item.id,
        name: item.name,
        remarks: item.remarks,
        status: item.status,
      });
      isEntryMode.value = false;
      localDialog.value = true;
    };

    const submit = handleSubmit((values) => {
      loading.value = true;
      store
        .dispatch("saveUpdateBonusType", values)
        .then((res) => {
          toast.success(res.data.message || "Saved successfully");
          resetForm();
          localDialog.value = false;
          store.dispatch("getBonusTypeList").finally(() => (loading.value = false));
        })
        .catch((err) => {
          loading.value = false;
          toast.error(err.response?.data?.message || "Something went wrong");
        });
    });

    const closeDialog = () => {
      localDialog.value = false;
      resetForm();
    };

    return {
      localDialog,
      isEntryMode,
      loading,
      name,
      remarks,
      status,
      bonus_type_id,
      submit,
      closeDialog,
      openAddBonusType,
      editItem,
    };
  },
  data() {
    return {
      panel: [0],
      itemsPerPage: 10,
      search: "",
      headers: [
        { title: "#SL", key: "index", align: "center", width: "7%", sortable: false },
        { title: "Code", key: "code", align: "left", sortable: false },
        { title: "Name", key: "name", align: "left", sortable: false },
        { title: "Remarks", key: "remarks", align: "left", sortable: true },
        { title: "Status", key: "status", align: "center", sortable: true },
        { title: "Actions", key: "actions", align: "center", sortable: false },
      ],
    };
  },
  computed: {
    getSchema() {
      return this.$store.getters.getBonusTypeList;
    },
  },
  created() {
    this.$store.dispatch("getBonusTypeList");
  },
};
</script>

<style scoped></style>
